/**
 * Page /admin/perimetres — Périmètres d'administration (« bascule DGS »).
 *
 * ─── LE BESOIN ──────────────────────────────────────────────────────────────
 *
 * « Le DGS pourrait être un admin à qui on affecte des briques. Un peu comme des
 *   adjoints qui auraient 2-3 briques en gestion, alors que le maire serait
 *   admin complet. »
 *
 * Jusqu'ici, il n'existait aucun cran entre le manager (1 service) et l'admin
 * (tout). Le DGS, le maire et l'adjoint avaient exactement les mêmes droits.
 *
 * ─── CE QUE L'ÉCRAN DIT, ET NE DIT PAS ──────────────────────────────────────
 *
 * Il n'affiche QUE les admins — un manager se règle par ses services, pas ici.
 *
 * « Accès complet » est le défaut et l'état normal du maire : l'écran le dit
 * explicitement plutôt que d'afficher une liste vide, qui laisserait croire à
 * une restriction.
 *
 * ⚠️ Réservé à un admin COMPLET (garde serveur `requireAdminComplet`). Un DGS
 * EST un admin : s'il pouvait ouvrir cet écran, il s'attribuerait les briques
 * manquantes en un clic.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type AdminPerimetre } from '@/lib/api';
import { ShieldCheck, Loader2, Info } from 'lucide-react';

/** Libellés métier — les clés techniques ne parlent pas à un maire. */
const BRIQUE_LABEL: Record<string, string> = {
  grc: 'Relation citoyen (GRC)',
  service_technique: 'Service technique',
  scolaire_cantine: 'Scolaire & cantine',
  associations: 'Associations',
  urbanisme: 'Urbanisme',
  comptable: 'Comptabilité',
  petite_enfance: 'Petite enfance',
  rh: 'Ressources humaines',
  agents: 'Mon dossier personnel',
};

export function AdminPerimetresPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [admins, setAdmins] = useState<AdminPerimetre[]>([]);
  const [briques, setBriques] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [edite, setEdite] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>([]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.admin.perimetres.list();
      setAdmins(r.admins); setBriques(r.briquesDisponibles); setError(null);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  function ouvrirEdition(a: AdminPerimetre) {
    setEdite(a.userId);
    // Un admin complet ouvre sur TOUT coché : c'est son état réel. Ouvrir sur
    // une liste vide donnerait l'impression qu'il n'a rien.
    setSelection(a.briques ?? [...briques]);
  }

  async function enregistrer(a: AdminPerimetre) {
    // Tout coché = aucune restriction. On envoie `null` plutôt que la liste
    // complète : sinon l'admin serait « restreint à tout », et perdrait
    // silencieusement les briques ajoutées plus tard à l'application.
    const toutCoche = selection.length === briques.length;
    const cible = toutCoche ? null : selection;

    if (cible !== null && cible.length > 0 && a.briques === null) {
      const ok = await confirm({
        title: `Restreindre ${a.nom} ?`,
        description:
          `${a.nom} n'aura plus accès qu'à ${cible.length} brique${cible.length > 1 ? 's' : ''} `
          + `sur ${briques.length}.\n\nIl ne pourra plus gérer les utilisateurs ni les rôles — `
          + `ces actions restent réservées aux administrateurs sans périmètre.`,
        confirmLabel: 'Restreindre',
      });
      if (!ok) return;
    }

    setBusy(a.userId); setError(null); setMsg(null);
    try {
      await api.admin.perimetres.set(a.userId, { briques: cible });
      setMsg('Périmètre enregistré.');
      setEdite(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setBusy(null); }
  }

  async function toggleAcces(a: AdminPerimetre, cle: 'vueMaire' | 'parametres') {
    setBusy(a.userId); setError(null); setMsg(null);
    try {
      await api.admin.perimetres.set(a.userId, {
        briques: a.briques, [cle]: !a[cle],
      });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <Alert variant="info" icon={Info}>
        Par défaut, un administrateur a <strong>accès à tout</strong> — c'est le cas
        du maire. Vous pouvez restreindre un adjoint ou un DGS à certaines briques :
        il reste administrateur, mais uniquement sur celles-ci.
        <br />
        <span className="text-xs">
          La gestion des utilisateurs, des rôles et des périmètres reste réservée aux
          administrateurs sans restriction.
        </span>
      </Alert>

      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ink-muted" />
        </div>
      ) : (
        <div className="space-y-3">
          {admins.map((a) => (
            <Card key={a.userId} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-tech" />
                    <span className="font-semibold text-ink">{a.nom}</span>
                    {a.briques === null
                      ? <Badge variant="success">Accès complet</Badge>
                      : <Badge variant="info">{a.briques.length} brique{a.briques.length > 1 ? 's' : ''}</Badge>}
                  </div>
                  <p className="mt-0.5 text-xs text-ink-muted">{a.email}</p>
                </div>
                {edite !== a.userId && (
                  <Button variant="secondary" size="sm" onClick={() => ouvrirEdition(a)}>
                    Modifier
                  </Button>
                )}
              </div>

              {edite === a.userId ? (
                <div className="mt-3 space-y-3 border-t border-border/60 pt-3">
                  <p className="text-xs text-ink-muted">
                    Cochez les briques que cette personne administre. Tout cocher =
                    accès complet.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {briques.map((b) => {
                      const on = selection.includes(b);
                      return (
                        <button key={b} type="button"
                          onClick={() => setSelection(on
                            ? selection.filter((x) => x !== b)
                            : [...selection, b])}
                          className={`rounded-r-pill border px-3 py-1.5 text-xs transition ${
                            on ? 'border-tech bg-tech-tint/60 text-tech-fg'
                               : 'border-border bg-surface/40 text-ink-muted hover:text-ink'}`}>
                          {BRIQUE_LABEL[b] ?? b}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEdite(null)}>Annuler</Button>
                    <Button variant="primary" size="sm"
                      onClick={() => void enregistrer(a)} disabled={busy === a.userId}>
                      {busy === a.userId ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
                  {a.briques !== null && (
                    <div className="flex flex-wrap gap-1">
                      {a.briques.map((b) => (
                        <Badge key={b} variant="neutral" size="sm">{BRIQUE_LABEL[b] ?? b}</Badge>
                      ))}
                    </div>
                  )}
                  {/* Deux accès qui ne sont PAS des briques : ils traversent tout. */}
                  <div className="flex flex-wrap gap-1.5">
                    {([['vueMaire', 'Vue Maire consolidée'], ['parametres', 'Paramètres de la commune']] as const).map(([k, lab]) => (
                      <button key={k} type="button"
                        onClick={() => void toggleAcces(a, k)}
                        disabled={busy === a.userId}
                        className={`rounded-r-pill border px-3 py-1 text-xs transition disabled:opacity-50 ${
                          a[k] ? 'border-tech/30 bg-tech-tint/40 text-tech-fg'
                               : 'border-border bg-surface/40 text-ink-muted line-through'}`}>
                        {lab}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {dialogElement}
    </div>
  );
}
